import PropTypes from 'prop-types'
import { useTheme } from '@emotion/react'
import clsx from 'clsx'
import { tint } from 'polished'
import {
  generateGradient,
  tealGreen,
} from 'chaoskit/src/assets/styles/utility/gradient'
import { Checkbox, Close, Input } from 'chaoskit/src/components'

import { textGradientUnderline } from '~styles/utility'

export const StylesRC = (theme) => ({
  background: '#1e2832',
  get panel() {
    return tint(0.1, this.background)
  },
  gradeSize: 150,
  underline: [
    textGradientUnderline(theme),
    {
      '&[role="button"]': {
        cursor: 'help',
      },

      '.is-active &': {
        backgroundImage: generateGradient({
          start: theme.contrast.base,
          stop: theme.contrast.base,
        }),
      },
    },
  ],
})

export const getDefaultSLOBasedOnTier = (step1) => {
  // eslint-disable-next-line default-case
  switch (step1) {
    case 'tier_1':
      return 99.999
    case 'tier_2':
      return 99.99
    case 'tier_3':
      return 99.9
  }
}

export const getGrade = (
  step1,
  step2,
  step3,
  step4,
  step5,
  step6,
  step7,
  step8,
  step9
) => {
  // Small utility used for final grading
  const getLetterGrade = (partIIScore, partIIIScore) => {
    let finalCalc = partIIScore * partIIIScore

    // Bumping up by percentage-factor based on tier level chosen
    if (step1 === 'tier_2') {
      // Increase by 30%
      finalCalc += finalCalc * 0.3
    } else if (step1 === 'teir_3') {
      // Increase by 70%
      finalCalc += finalCalc * 0.7
    }

    finalCalc = finalCalc.toFixed(2)

    if (finalCalc < 0.06) {
      return 'F'
    }
    if (finalCalc >= 0.06 && finalCalc <= 0.08) {
      return 'C-'
    }
    if (finalCalc >= 0.09 && finalCalc <= 0.11) {
      return 'C'
    }
    if (finalCalc >= 0.12 && finalCalc <= 0.14) {
      return 'C+'
    }
    if (finalCalc >= 0.15 && finalCalc <= 0.24) {
      return 'B-'
    }
    if (finalCalc >= 0.25 && finalCalc <= 0.39) {
      return 'B'
    }
    if (finalCalc >= 0.4 && finalCalc <= 0.49) {
      return 'B+'
    }
    if (finalCalc >= 0.5 && finalCalc <= 0.54) {
      return 'A-'
    }
    if (finalCalc >= 0.55 && finalCalc <= 0.79) {
      return 'A'
    }
    if (finalCalc >= 0.8) {
      return 'A+'
    }
  }

  // Splitting up scores into separate sections as they are used to influence the recommended articles at the very end

  // Part II scores include steps 2, 3, and 4
  const partIIAvailability =
    Math.abs(parseInt(step2, 10)) /
    (Math.abs(parseInt(step2, 10)) + Math.abs(parseInt(step3, 10)))

  const getPartIISLOAttain = () => {
    let step4Clean = step4

    // Clean up dirty data user could provide
    if (parseInt(step4, 10) === 0 || parseInt(step4, 10) === '') {
      step4Clean = getDefaultSLOBasedOnTier(step1)
    }

    // Convert percentage to decimal value
    step4Clean /= 100

    if (step4Clean > 1) {
      step4Clean = 1
    }

    return step4Clean
  }

  const partIIScore = () => {
    // If either step2 or step3 are 0 (they checked the box), then set default value for partII
    if (parseInt(step2, 10) === 0 || parseInt(step3, 10) === 0) {
      return 0.25
    }

    return partIIAvailability / getPartIISLOAttain()
  }

  // Part III score contains 5, 6, 7, 8, and 9
  const getStep5Score = () => {
    // eslint-disable-next-line default-case
    switch (step5) {
      case 'no':
        return 0
      case 'yes':
        return 0.15
    }
  }

  const getStep6Score = () => {
    // eslint-disable-next-line default-case
    switch (step6) {
      case 'no':
        return 0
      case 'yes':
        return 0.1
    }
  }

  const getStep7Score = () => {
    // eslint-disable-next-line default-case
    switch (step7) {
      case 'no':
        return 0
      case 'yes-247':
        return 0.25
      case 'yes-business':
        return 0.1
    }
  }

  const getStep8Score = () => {
    // eslint-disable-next-line default-case
    switch (step8) {
      case 'no':
        return 0
      case 'yes-monitoring-alerting':
        return 0.25
      case 'yes-monitoring':
        return 0.1
    }
  }

  const getStep9Score = () => {
    // eslint-disable-next-line default-case
    switch (step9) {
      case 'never':
        return 0
      case 'annually':
        return 0.05
      case 'quarterly':
        return 0.1
      case 'monthly':
        return 0.15
      case 'weekly':
        return 0.2
      case 'continuously':
        return 0.25
    }
  }

  const partIIIScore =
    getStep5Score() +
    getStep6Score() +
    getStep7Score() +
    getStep8Score() +
    getStep9Score()

  return getLetterGrade(partIIScore(), partIIIScore)
}

export const ReliabilityCell = ({ className, ...rest }) => {
  const theme = useTheme()

  return (
    <div
      className={clsx(theme.settings.classes.trim, className)}
      css={{ width: '100%' }}
      {...rest}
    />
  )
}

ReliabilityCell.propTypes = {
  className: PropTypes.string,
}

export const ReliabilityGrid = ({ size, ...rest }) => {
  const theme = useTheme()

  return (
    <div
      css={{
        display: 'grid',
        gap: theme.space.base,

        [theme.mq.medium]: {
          gap: theme.space.large,
          gridTemplateColumns: `repeat(${size}, 1fr)`,
        },
      }}
      {...rest}
    />
  )
}

ReliabilityGrid.propTypes = {
  size: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
}

export const ReliabilityHelpTrigger = (props) => {
  const theme = useTheme()

  return (
    <span
      role="button"
      tabIndex="-1"
      css={StylesRC(theme).underline}
      {...props}
    />
  )
}

export const ReliabilityOption = ({ title, children, active, ...rest }) => {
  const theme = useTheme()

  return (
    <div
      css={{
        textAlign: 'center',
        background: theme.color.light.base,
        borderRadius: theme.borderRadius.base,
        cursor: 'pointer',
        boxShadow: theme.boxShadow.large,
        zIndex: 1,
        padding: theme.space.base,

        [theme.mq.medium]: {
          padding: theme.space.large,
        },

        '&.is-active': {
          ...tealGreen(theme),
        },
      }}
      className={clsx(theme.settings.classes.trim, {
        [`${theme.settings.classes.active} u-contrast`]: active,
      })}
      tabIndex="-1"
      role="button"
      {...rest}
    >
      <h4
        css={[
          children && [
            StylesRC(theme).underline,
            {
              paddingBottom: theme.space.small,
            },
          ],
        ]}
      >
        {title}
      </h4>
      {children}
    </div>
  )
}

ReliabilityOption.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node,
  active: PropTypes.bool,
}

export const ReliabilityHelp = ({ children, title, toggleHelpOpen }) => {
  const theme = useTheme()

  return (
    <div
      css={{
        position: 'relative',
        background: StylesRC(theme).panel,
        borderRadius: theme.borderRadius.base,
        marginBottom: theme.space.large,
        padding: theme.space.base,

        [theme.mq.medium]: {
          padding: theme.space.large,
        },
      }}
      className={`u-contrast ${theme.settings.classes.trim}`}
    >
      <h3
        css={{
          marginBottom: theme.space.large,
          textAlign: 'center',
        }}
      >
        <span css={StylesRC(theme).underline}>{title}</span>
      </h3>
      <Close
        css={{
          position: 'absolute',
          top: theme.space.base,
          right: theme.space.base,
          zIndex: 1,
        }}
        onClick={() => toggleHelpOpen(false)}
      />
      {children}
    </div>
  )
}

ReliabilityHelp.propTypes = {
  children: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
  toggleHelpOpen: PropTypes.func.isRequired,
}

export const ReliabilityCheckbox = (props) => {
  const theme = useTheme()

  return (
    <div
      css={{
        marginTop: theme.space.large,
        display: 'inline-grid',
        position: 'relative',
        left: '50%',
        transform: 'translateX(-50%)',

        span: {
          alignSelf: 'center',
          fontSize: theme.fontSize.small,
          letterSpacing: theme.letterSpacing.extended,
          textTransform: 'uppercase',
          fontWeight: theme.fontWeight.bold,
        },
      }}
    >
      <Checkbox {...props} />
    </div>
  )
}

export const ReliabilityInput = ({ name, defaultValue, ...rest }) => {
  const theme = useTheme()

  return (
    <Input
      css={{
        fontWeight: theme.fontWeight.bold,
        fontSize: theme.fontSize.xlarge,
        height: theme.height.large,
        textAlign: 'center',
        paddingTop: 0, // Remove top-padding that normally takes up space for label
      }}
      wrapperProps={{
        css: {
          border: 0,

          '.CK__FormFooter': {
            justifyContent: 'center',
            marginTop: theme.space.base,

            div: {
              color: theme.contrast.muted,
            },
          },
        },
      }}
      name={name}
      type="number"
      defaultValue={defaultValue}
      min="0"
      onChange={() => {}}
      {...rest}
    />
  )
}

ReliabilityInput.propTypes = {
  name: PropTypes.string.isRequired,
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
}
